'use client'

import Link from 'next/link'
import {useTranslations} from 'next-intl'
import type {ComponentProps, MouseEvent} from 'react'
import {Icon} from '@/design-system'
import {capture, type TrackedEvent} from '@/lib/analytics'

/**
 * A link that records analytics events when it is followed (MEAS-10).
 *
 * The cards and the requirements table are server components, and an
 * `onClick` cannot cross that boundary. This island is the smallest piece
 * that can: the events travel as plain data, and the link renders exactly
 * as it would have without them.
 *
 * Middle-clicks count too (`onAuxClick`), because "open in a new tab" is how
 * a lot of students read down a results list.
 */
function track(events: TrackedEvent[]) {
  for (const event of events) capture(event.name, event.properties as never)
}

function trackAux(events: TrackedEvent[]) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) track(events)
  }
}

/** An in-app route. */
export function TrackedLink({
  events,
  onClick,
  ...props
}: ComponentProps<typeof Link> & {events: TrackedEvent[]}) {
  return (
    <Link
      {...props}
      onClick={(event) => {
        track(events)
        onClick?.(event)
      }}
      onAuxClick={trackAux(events)}
    />
  )
}

/**
 * A page outside Uniap, always in a new tab.
 *
 * `noopener noreferrer` is not optional here: every destination is a URL a
 * search engine or a source returned, and none of them gets a handle on this
 * window or learns which search sent the student.
 *
 * It says so (SRCH-14): an icon for sighted readers and the same words for a
 * screen reader, because a tab that opens without warning reads as the page
 * having gone somewhere.
 */
export function TrackedAnchor({
  events,
  onClick,
  children,
  ...props
}: ComponentProps<'a'> & {href: string; events: TrackedEvent[]}) {
  const t = useTranslations('common')

  return (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        track(events)
        onClick?.(event)
      }}
      onAuxClick={trackAux(events)}
    >
      {children}
      <Icon
        name="external-link"
        size={14}
        style={{marginInlineStart: '0.3em', verticalAlign: '-0.1em'}}
      />
      <span className="u-sr-only"> ({t('opensInNewTab')})</span>
    </a>
  )
}
